import { http } from '@pendo/http';

const BULK_DELETE_ENDPOINT = '/api/s/_SID_/user/bulk/v2/removeUserFromSubscription';
const REQUEST_ID_SUFFIX = 'bulk-delete-user';

export const DEFAULT_POLL_INTERVAL_MS = 5000;

export const DEFAULT_POLL_MAX_ATTEMPTS = 240;

export function buildBulkDeleteRequestId ({ subscriptionId, userId }) {
    return `${subscriptionId}:${userId}:${REQUEST_ID_SUFFIX}`;
}

export async function getBulkDeleteStatus (requestId) {
    try {
        const res = await http.get(`${BULK_DELETE_ENDPOINT}/status/${requestId}`);

        return res.data;
    } catch (err) {
        if (err?.response?.status === 404) {
            return { status: 'none' };
        }
        throw err;
    }
}

export function pollBulkDeleteStatus ({
    requestId,
    intervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts = DEFAULT_POLL_MAX_ATTEMPTS,
    signal,
    onInProgress,
} = {}) {
    return new Promise((resolve, reject) => {
        let attempts = 0;
        let timeoutId = null;

        function cleanup () {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            if (signal) {
                signal.removeEventListener('abort', onAbort);
            }
        }

        function onAbort () {
            cleanup();
            const reason = signal?.reason || new Error('Bulk delete polling aborted');
            reject(reason);
        }

        if (signal) {
            if (signal.aborted) {
                onAbort();

                return;
            }
            signal.addEventListener('abort', onAbort, { once: true });
        }

        const tick = async () => {
            if (signal?.aborted) {
                onAbort();

                return;
            }

            attempts += 1;

            try {
                const res = await http.get(`${BULK_DELETE_ENDPOINT}/status/${requestId}`);

                if (signal?.aborted) {
                    cleanup();

                    return;
                }

                const body = res?.data || {};

                if (body.status === 'completed') {
                    cleanup();
                    resolve({ status: 'completed', results: body.results || [] });

                    return;
                }

                if (body.status === 'failed') {
                    cleanup();
                    resolve({ status: 'failed', error: body.error || 'Bulk delete failed' });

                    return;
                }

                if (body.status === 'in_progress' && typeof onInProgress === 'function') {
                    onInProgress();
                }

                if (attempts >= maxAttempts) {
                    cleanup();
                    reject(new Error('Bulk delete polling exceeded max attempts'));

                    return;
                }

                timeoutId = setTimeout(tick, intervalMs);
            } catch (err) {
                cleanup();
                reject(err);
            }
        };

        tick();
    });
}
